/**
 * EngagementHistogramContainer
 * 
 * A container that manages engagement histogram state and data transformation.
 * Similar to ConvSuccessHistogramContainer but uses engagement analytics APIs
 * for organization-level histogram data.
 * 
 * Supports multiple histogram views:
 * - All Conversations: Shows all calls, containable, and contained lines
 * - Containable Conversations: Shows containable and contained lines
 * - Spanish Calls: Shows Spanish-specific call volume lines
 * - Average Calls by Hour: Shows average call distribution by hour
 * - Average Calls by Day of Week: Shows average call distribution by day
 * - Concurrent Calls: Shows concurrent call volume over time
 */

import { useMemo, useState, useCallback, useEffect } from "react";
import { createContainer } from "unstated-next";
import moment from "moment";
import HistogramModel from "../models/analytics/HistogramModel";
import { chartSequentialBlues } from "../constants/chartTheme";
import {
    transformHistogramsToMultiLineData,
    transformSpanishHistogramsToMultiLineData,
    transformAverageByHourHistogramToDataset,
    transformAverageByDayHistogramToDataset,
    transformConcurrentHistogramToDataset,
    transformOrderVolumeToMultiLineData,
    HistogramDataset,
    parseAnalyticsDate
} from "../utils/engagementAnalyticsTransformers";

// ==================== TYPES ====================

/**
 * Available histogram view options
 */
export type EngagementHistogramView =
    | "All Conversations"
    | "Containable Conversations"
    | "Order Calls"
    | "Spanish Calls"
    | "Average Calls by Hour"
    | "Average Calls by Day of Week"
    | "Concurrent Calls";

/**
 * Parameters for the engagement histogram container
 */
export interface EngagementHistogramParams {
    organizationId?: string;
    startDate: string;      // ISO format or "YYYY-MM-DD HH:mm"
    endDate: string;        // ISO format or "YYYY-MM-DD HH:mm"
    applicationIds?: string[];
    title?: string;
    titleStyles?: React.CSSProperties;
    subtitle?: string;
    scale?: number;
    selectedView?: EngagementHistogramView;
}

/**
 * Histogram data from OrgAnalyticsContainer
 */
export interface EngagementHistogramData {
    // Regular volume histograms
    callVolumeHistogram: HistogramModel | null;
    containableCallVolumeHistogram: HistogramModel | null;
    containedCallVolumeHistogram: HistogramModel | null;
    // Order volume histograms
    orderAttemptedVolumeHistogram: HistogramModel | null;
    orderPlacedVolumeHistogram: HistogramModel | null;
    // Spanish histograms
    spanishCallVolumeHistogram: HistogramModel | null;
    spanishContainableCallVolumeHistogram: HistogramModel | null;
    spanishContainedCallVolumeHistogram: HistogramModel | null;
    // Average histograms (separate for hour and day of week)
    averageCallVolumeByHourHistogram: HistogramModel | null;
    averageCallVolumeByDayHistogram: HistogramModel | null;
    // Concurrent histogram
    concurrentCallVolumeHistogram: HistogramModel | null;
}

/**
 * Loading state for histogram data
 */
export interface EngagementHistogramLoadingState {
    loadingCallVolume: boolean;
    loadingContainable: boolean;
    loadingContained: boolean;
    loadingOrderAttempted: boolean;
    loadingOrderPlaced: boolean;
    loadingSpanishCallVolume: boolean;
    loadingSpanishContainable: boolean;
    loadingSpanishContained: boolean;
    loadingAverageCallVolumeByHour: boolean;
    loadingAverageCallVolumeByDay: boolean;
    loadingConcurrentCallVolume: boolean;
}

/**
 * Initial state for the container
 */
type InitialState = Partial<EngagementHistogramParams>;

// ==================== COLOR CONFIGURATION ====================

const DEFAULT_COLORS = {
    allCalls: chartSequentialBlues[0],
    containable: chartSequentialBlues[1],
    contained: chartSequentialBlues[2]
};

const SPANISH_COLORS = {
    spanishCalls: chartSequentialBlues[0],
    spanishContainable: chartSequentialBlues[1],
    spanishContained: chartSequentialBlues[2]
};

const ORDER_COLORS = {
    allCalls: chartSequentialBlues[0],
    orderAttempted: chartSequentialBlues[1],
    orderPlaced: chartSequentialBlues[2]
};

// ==================== HELPER FUNCTIONS ====================

/**
 * Format date string for display in subtitle
 */
const formatDateForSubtitle = (dateStr: string): string => {
    if (!dateStr) return "";
    return moment(dateStr).format("MMM D");
};

/**
 * Get interval for API based on date range
 * For regular volume/concurrent: 0=hour, 1=day, 2=week, 3=month
 */
export const getVolumeInterval = (startDate: string, endDate: string): number => {
    // Use startOf('day') to compare calendar days, ignoring time portion
    const start = parseAnalyticsDate(startDate).startOf('day');
    const end = parseAnalyticsDate(endDate).startOf('day');
    if (!start.isValid() || !end.isValid()) return 1;
    const daysDiff = end.diff(start, 'days');

    // Same day = hourly (0) - shows hourly breakdown for single day selection
    if (daysDiff === 0) return 0;
    // 1-30 days = daily (1)
    if (daysDiff <= 30) return 1;
    // 31+ days = monthly (3)
    return 3;
};

/**
 * Get interval for average volume histograms based on date range
 * For average volume: 0=hour, 1=day of week, 2=month
 */
export const getAverageInterval = (startDate: string, endDate: string): number => {
    // Use startOf('day') to compare calendar days, ignoring time portion
    const start = parseAnalyticsDate(startDate).startOf('day');
    const end = parseAnalyticsDate(endDate).startOf('day');
    if (!start.isValid() || !end.isValid()) return 1;
    const daysDiff = end.diff(start, 'days');

    // Same day = hourly (0) - shows hourly breakdown for single day selection
    if (daysDiff === 0) return 0;
    // Up to 30 days = day of week (1)
    if (daysDiff <= 30) return 1;
    // 31+ days = monthly (2)
    return 2;
};

// ==================== CONTAINER HOOK ====================

function useEngagementHistogramContainer(initialState: InitialState = {}) {
    // --- Params state
    const [params, setParams] = useState<EngagementHistogramParams>({
        organizationId: initialState.organizationId,
        startDate: initialState.startDate ?? "",
        endDate: initialState.endDate ?? "",
        applicationIds: initialState.applicationIds ?? [],
        title: initialState.title,
        titleStyles: initialState.titleStyles,
        subtitle: initialState.subtitle,
        scale: initialState.scale,
        selectedView: initialState.selectedView ?? "All Conversations"
    });

    // --- Histogram data state (injected from OrgAnalyticsContainer)
    const [histogramData, setHistogramData] = useState<EngagementHistogramData>({
        callVolumeHistogram: null,
        containableCallVolumeHistogram: null,
        containedCallVolumeHistogram: null,
        orderAttemptedVolumeHistogram: null,
        orderPlacedVolumeHistogram: null,
        spanishCallVolumeHistogram: null,
        spanishContainableCallVolumeHistogram: null,
        spanishContainedCallVolumeHistogram: null,
        averageCallVolumeByHourHistogram: null,
        averageCallVolumeByDayHistogram: null,
        concurrentCallVolumeHistogram: null
    });

    // --- Loading state (injected from OrgAnalyticsContainer)
    const [loadingState, setLoadingState] = useState<EngagementHistogramLoadingState>({
        loadingCallVolume: false,
        loadingContainable: false,
        loadingContained: false,
        loadingOrderAttempted: false,
        loadingOrderPlaced: false,
        loadingSpanishCallVolume: false,
        loadingSpanishContainable: false,
        loadingSpanishContained: false,
        loadingAverageCallVolumeByHour: false,
        loadingAverageCallVolumeByDay: false,
        loadingConcurrentCallVolume: false
    });

    // --- Setters for external data injection
    const updateHistogramData = useCallback((data: Partial<EngagementHistogramData>) => {
        setHistogramData(prev => ({ ...prev, ...data }));
    }, []);

    const updateLoadingState = useCallback((state: Partial<EngagementHistogramLoadingState>) => {
        setLoadingState(prev => ({ ...prev, ...state }));
    }, []);

    // --- View management
    const setSelectedView = useCallback((view: EngagementHistogramView) => {
        setParams(prev => ({ ...prev, selectedView: view }));
    }, []);

    // --- Check loading state based on selected view
    const isLoading = useMemo((): boolean => {
        switch (params.selectedView) {
            case 'All Conversations':
            case 'Containable Conversations':
                return loadingState.loadingCallVolume ||
                    loadingState.loadingContainable ||
                    loadingState.loadingContained;
            case 'Order Calls':
                return loadingState.loadingCallVolume ||
                    loadingState.loadingOrderAttempted ||
                    loadingState.loadingOrderPlaced;
            case 'Spanish Calls':
                return loadingState.loadingSpanishCallVolume ||
                    loadingState.loadingSpanishContainable ||
                    loadingState.loadingSpanishContained;
            case 'Average Calls by Hour':
                return loadingState.loadingAverageCallVolumeByHour;
            case 'Average Calls by Day of Week':
                return loadingState.loadingAverageCallVolumeByDay;
            case 'Concurrent Calls':
                return loadingState.loadingConcurrentCallVolume;
            default:
                return false;
        }
    }, [params.selectedView, loadingState]);

    // --- Transform data based on selected view
    const currentHistogramData = useMemo((): HistogramDataset[] | null => {
        // Return null while loading to indicate loading state
        if (isLoading) {
            return null;
        }

        switch (params.selectedView) {
            case 'All Conversations': {
                const { callVolumeHistogram, containableCallVolumeHistogram, containedCallVolumeHistogram } = histogramData;

                const hasData =
                    (callVolumeHistogram?.data?.length ?? 0) > 0 ||
                    (containableCallVolumeHistogram?.data?.length ?? 0) > 0 ||
                    (containedCallVolumeHistogram?.data?.length ?? 0) > 0;

                if (hasData) {
                    return transformHistogramsToMultiLineData(
                        callVolumeHistogram,
                        containableCallVolumeHistogram,
                        containedCallVolumeHistogram,
                        DEFAULT_COLORS
                    );
                }
                return [];
            }

            case 'Containable Conversations': {
                const { containableCallVolumeHistogram, containedCallVolumeHistogram } = histogramData;

                const hasData =
                    (containableCallVolumeHistogram?.data?.length ?? 0) > 0 ||
                    (containedCallVolumeHistogram?.data?.length ?? 0) > 0;

                if (hasData) {
                    // Only show containable and contained lines (no "All Conversations" line)
                    return transformHistogramsToMultiLineData(
                        null,
                        containableCallVolumeHistogram,
                        containedCallVolumeHistogram,
                        DEFAULT_COLORS
                    );
                }
                return [];
            }

            case 'Order Calls': {
                const { callVolumeHistogram, orderAttemptedVolumeHistogram, orderPlacedVolumeHistogram } = histogramData;

                const hasData =
                    (callVolumeHistogram?.data?.length ?? 0) > 0 ||
                    (orderAttemptedVolumeHistogram?.data?.length ?? 0) > 0 ||
                    (orderPlacedVolumeHistogram?.data?.length ?? 0) > 0;

                if (hasData) {
                    return transformOrderVolumeToMultiLineData(
                        callVolumeHistogram,
                        orderAttemptedVolumeHistogram,
                        orderPlacedVolumeHistogram,
                        ORDER_COLORS
                    );
                }
                return [];
            }

            case 'Spanish Calls': {
                const { spanishCallVolumeHistogram, spanishContainableCallVolumeHistogram, spanishContainedCallVolumeHistogram } = histogramData;

                const hasData =
                    (spanishCallVolumeHistogram?.data?.length ?? 0) > 0 ||
                    (spanishContainableCallVolumeHistogram?.data?.length ?? 0) > 0 ||
                    (spanishContainedCallVolumeHistogram?.data?.length ?? 0) > 0;

                if (hasData) {
                    return transformSpanishHistogramsToMultiLineData(
                        spanishCallVolumeHistogram,
                        spanishContainableCallVolumeHistogram,
                        spanishContainedCallVolumeHistogram,
                        SPANISH_COLORS
                    );
                }
                return [];
            }

            case 'Average Calls by Hour': {
                const { averageCallVolumeByHourHistogram } = histogramData;

                if (averageCallVolumeByHourHistogram?.data?.length) {
                    const dataset = transformAverageByHourHistogramToDataset(
                        averageCallVolumeByHourHistogram,
                        'Average Calls',
                        DEFAULT_COLORS.allCalls
                    );
                    return dataset.data.length > 0 ? [dataset] : [];
                }
                return [];
            }

            case 'Average Calls by Day of Week': {
                const { averageCallVolumeByDayHistogram } = histogramData;

                if (averageCallVolumeByDayHistogram?.data?.length) {
                    const dataset = transformAverageByDayHistogramToDataset(
                        averageCallVolumeByDayHistogram,
                        'Average Calls',
                        DEFAULT_COLORS.allCalls
                    );
                    return dataset.data.length > 0 ? [dataset] : [];
                }
                return [];
            }

            case 'Concurrent Calls': {
                const { concurrentCallVolumeHistogram } = histogramData;

                if (concurrentCallVolumeHistogram?.data?.length) {
                    const dataset = transformConcurrentHistogramToDataset(
                        concurrentCallVolumeHistogram,
                        'Max Concurrent Calls',
                        DEFAULT_COLORS.allCalls
                    );
                    return dataset.data.length > 0 ? [dataset] : [];
                }
                return [];
            }

            default:
                return [];
        }
    }, [params.selectedView, histogramData, isLoading]);

    // --- Derived title and subtitle
    const { title, subtitle } = useMemo(() => {
        const formattedStart = formatDateForSubtitle(params.startDate);
        const formattedEnd = formatDateForSubtitle(params.endDate);

        const defaultTitle = params.selectedView || "Call Volume";
        const title = params.title ?? defaultTitle;

        let subtitle = params.subtitle;
        if (!subtitle && formattedStart && formattedEnd) {
            subtitle = formattedStart === formattedEnd
                ? formattedStart
                : `${formattedStart} - ${formattedEnd}`;
        }

        return { title, subtitle: subtitle || "" };
    }, [params.title, params.subtitle, params.startDate, params.endDate, params.selectedView]);

    // --- For backward compatibility, provide mappedData as the first dataset's data
    const mappedData = currentHistogramData?.[0]?.data || [];

    return {
        // Params and state
        params,
        setParams,

        // View management
        selectedView: params.selectedView,
        setSelectedView,

        // Data injection methods (for syncing with OrgAnalyticsContainer)
        updateHistogramData,
        updateLoadingState,
        histogramData,
        loadingState,

        // Transformed data for charts
        currentHistogramData,
        mappedData,           // Backward compatible - single line data
        mappedDatasets: currentHistogramData || [], // Multi-line data

        // Display properties
        title,
        titleStyles: params.titleStyles,
        subtitle,
        scale: params.scale,

        // Loading state
        isLoading,
        loading: isLoading,
        error: null // Errors handled by OrgAnalyticsContainer
    };
}

// ==================== CONTAINER EXPORT ====================

const EngagementHistogramContainer = createContainer(useEngagementHistogramContainer);
export default EngagementHistogramContainer;

// ==================== SYNC HOOK ====================

/**
 * Hook to sync EngagementHistogramContainer with OrgAnalyticsContainer data
 * Use this in your component to connect the two containers
 * 
 * Example usage:
 * ```tsx
 * const orgAnalytics = OrgAnalyticsContainer.useContainer();
 * const engagementHistogram = EngagementHistogramContainer.useContainer();
 * 
 * useSyncEngagementHistogram(orgAnalytics, engagementHistogram);
 * ```
 */
export function useSyncEngagementHistogram(
    orgAnalytics: {
        organizationCallVolumeHistogram: HistogramModel | null;
        organizationContainableCallVolumeHistogram: HistogramModel | null;
        organizationContainedCallVolumeHistogram: HistogramModel | null;
        organizationSpanishCallVolumeHistogram: HistogramModel | null;
        organizationSpanishContainableCallVolumeHistogram: HistogramModel | null;
        organizationSpanishContainedCallVolumeHistogram: HistogramModel | null;
        organizationAverageCallVolumeHistogram: HistogramModel | null;
        organizationAverageCallVolumeByHourHistogram: HistogramModel | null;
        organizationConcurrentCallVolumeHistogram: HistogramModel | null;
        loadingOrganizationCallVolumeHistogram: boolean;
        loadingOrganizationContainableCallVolumeHistogram: boolean;
        loadingOrganizationContainedCallVolumeHistogram: boolean;
        loadingOrganizationSpanishCallVolumeHistogram: boolean;
        loadingOrganizationSpanishContainableCallVolumeHistogram: boolean;
        loadingOrganizationSpanishContainedCallVolumeHistogram: boolean;
        loadingOrganizationAverageCallVolumeHistogram: boolean;
        loadingOrganizationAverageCallVolumeByHourHistogram: boolean;
        loadingOrganizationConcurrentCallVolumeHistogram: boolean;
    },
    engagementHistogram: {
        updateHistogramData: (data: Partial<EngagementHistogramData>) => void;
        updateLoadingState: (state: Partial<EngagementHistogramLoadingState>) => void;
    }
) {
    // Sync histogram data
    useEffect(() => {
        engagementHistogram.updateHistogramData({
            callVolumeHistogram: orgAnalytics.organizationCallVolumeHistogram,
            containableCallVolumeHistogram: orgAnalytics.organizationContainableCallVolumeHistogram,
            containedCallVolumeHistogram: orgAnalytics.organizationContainedCallVolumeHistogram,
            spanishCallVolumeHistogram: orgAnalytics.organizationSpanishCallVolumeHistogram,
            spanishContainableCallVolumeHistogram: orgAnalytics.organizationSpanishContainableCallVolumeHistogram,
            spanishContainedCallVolumeHistogram: orgAnalytics.organizationSpanishContainedCallVolumeHistogram,
            averageCallVolumeByHourHistogram: orgAnalytics.organizationAverageCallVolumeByHourHistogram,
            averageCallVolumeByDayHistogram: orgAnalytics.organizationAverageCallVolumeHistogram,
            concurrentCallVolumeHistogram: orgAnalytics.organizationConcurrentCallVolumeHistogram
        });
    }, [
        orgAnalytics.organizationCallVolumeHistogram,
        orgAnalytics.organizationContainableCallVolumeHistogram,
        orgAnalytics.organizationContainedCallVolumeHistogram,
        orgAnalytics.organizationSpanishCallVolumeHistogram,
        orgAnalytics.organizationSpanishContainableCallVolumeHistogram,
        orgAnalytics.organizationSpanishContainedCallVolumeHistogram,
        orgAnalytics.organizationAverageCallVolumeHistogram,
        orgAnalytics.organizationAverageCallVolumeByHourHistogram,
        orgAnalytics.organizationConcurrentCallVolumeHistogram
    ]);

    // Sync loading state
    useEffect(() => {
        engagementHistogram.updateLoadingState({
            loadingCallVolume: orgAnalytics.loadingOrganizationCallVolumeHistogram,
            loadingContainable: orgAnalytics.loadingOrganizationContainableCallVolumeHistogram,
            loadingContained: orgAnalytics.loadingOrganizationContainedCallVolumeHistogram,
            loadingSpanishCallVolume: orgAnalytics.loadingOrganizationSpanishCallVolumeHistogram,
            loadingSpanishContainable: orgAnalytics.loadingOrganizationSpanishContainableCallVolumeHistogram,
            loadingSpanishContained: orgAnalytics.loadingOrganizationSpanishContainedCallVolumeHistogram,
            loadingAverageCallVolumeByHour: orgAnalytics.loadingOrganizationAverageCallVolumeByHourHistogram,
            loadingAverageCallVolumeByDay: orgAnalytics.loadingOrganizationAverageCallVolumeHistogram,
            loadingConcurrentCallVolume: orgAnalytics.loadingOrganizationConcurrentCallVolumeHistogram
        });
    }, [
        orgAnalytics.loadingOrganizationCallVolumeHistogram,
        orgAnalytics.loadingOrganizationContainableCallVolumeHistogram,
        orgAnalytics.loadingOrganizationContainedCallVolumeHistogram,
        orgAnalytics.loadingOrganizationSpanishCallVolumeHistogram,
        orgAnalytics.loadingOrganizationSpanishContainableCallVolumeHistogram,
        orgAnalytics.loadingOrganizationSpanishContainedCallVolumeHistogram,
        orgAnalytics.loadingOrganizationAverageCallVolumeHistogram,
        orgAnalytics.loadingOrganizationAverageCallVolumeByHourHistogram,
        orgAnalytics.loadingOrganizationConcurrentCallVolumeHistogram
    ]);
}
